import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { CheckCircle, Star, Gauge } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_LABELS = ["", "Très insatisfait", "Insatisfait", "Correct", "Satisfait", "Excellent"];

// Preset scales — each entry: [icon_emoji, tone_color_class_selected, ring_color_class_selected]
const SCALE_PRESETS = {
  stars: null, // fallback rendering with Star icon
  smileys: ["😠", "😕", "😐", "🙂", "😍"],
  hearts: ["💔", "😔", "🙂", "😊", "❤️"],
  thumbs: ["👎", "👎", "👐", "👍", "👍"],
};

function ScaleRow({ label, value, onChange, testid, style = "stars", labels = DEFAULT_LABELS }) {
  const emojis = SCALE_PRESETS[style] || null;
  return (
    <div className="mt-6">
      <div className="text-sm font-medium mb-2" data-testid={`${testid}-label`}>{label}</div>
      <div className="flex items-center gap-2 flex-wrap" data-testid={testid}>
        {[1, 2, 3, 4, 5].map((s) => (
          <div key={s} className="flex flex-col items-center gap-1">
            <button
              type="button" onClick={() => onChange(s)}
              data-testid={`${testid}-${s}`}
              className={`w-12 h-12 rounded-full border-2 flex items-center justify-center text-2xl transition-all ${
                value === s
                  ? "border-primary ring-2 ring-primary/30 bg-primary/5 scale-110"
                  : "border-border bg-background hover:border-primary/40 opacity-60 hover:opacity-100"
              }`}
            >
              {emojis ? (
                <span>{emojis[s - 1]}</span>
              ) : (
                <Star weight={value >= s ? "fill" : "regular"} size={22} className={value >= s ? "text-amber-500" : "text-muted-foreground"}/>
              )}
            </button>
            {labels[s] && <span className="text-[10px] uppercase tracking-wider text-muted-foreground text-center max-w-[80px] leading-tight">{labels[s]}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Satisfaction() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const initialScore = parseInt(params.get("score") || "0", 10);
  const [ticket, setTicket] = useState(null);
  const [answers, setAnswers] = useState({});
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) { setError("Lien invalide"); return; }
    axios.get(`${API}/satisfaction/${token}`)
      .then((r) => {
        setTicket(r.data);
        if (r.data.submitted) {
          setDone(true);
          setAnswers(r.data.answers || {});
        } else if (initialScore && r.data.questions?.length) {
          // Preseed all questions with the score coming from the email button
          const seed = {};
          for (const q of r.data.questions) seed[q.id] = initialScore;
          setAnswers(seed);
        }
      })
      .catch(() => setError("Lien invalide ou expiré"));
  }, [token, initialScore]);

  const questions = ticket?.questions || [];
  const allAnswered = questions.length > 0 && questions.every((q) => answers[q.id] >= 1 && answers[q.id] <= 5);
  const overall = allAnswered
    ? Math.round(questions.reduce((s, q) => s + (answers[q.id] || 0), 0) / questions.length)
    : 0;

  const submit = async () => {
    if (!allAnswered) return;
    setBusy(true);
    try {
      await axios.post(`${API}/satisfaction`, { token, answers, comment });
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
            <Gauge weight="bold" size={20} className="text-white"/>
          </div>
          <div>
            <div className="font-display font-bold">MasterTicket</div>
            <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">Enquête satisfaction</div>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <Card className="w-full max-w-xl">
          <CardContent className="pt-10 pb-10 px-8">
            {error && <p className="text-destructive text-center" data-testid="survey-error">{error}</p>}
            {!error && !ticket && <p className="text-muted-foreground text-center">Chargement…</p>}
            {ticket && done && (
              <div className="text-center" data-testid="survey-done">
                <CheckCircle weight="fill" size={64} className="text-emerald-500 mx-auto"/>
                <h1 className="font-display text-3xl font-bold mt-4">Merci pour votre retour</h1>
                <p className="text-muted-foreground mt-2">
                  {ticket.score ? `Votre évaluation (${ticket.score}/5) a bien été enregistrée.` : "Votre évaluation a bien été enregistrée."}
                </p>
                {Object.keys(answers).length > 0 && questions.length > 0 && (
                  <div className="mt-6 text-left border-t pt-4 space-y-1">
                    {questions.map((q) => (
                      <div key={q.id} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{q.label}</span>
                        <span className="mono font-medium">{answers[q.id] || "—"}/5</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {ticket && !done && (
              <div>
                <div className="label-caps">Ticket #{ticket.number}</div>
                <h1 className="font-display text-2xl font-bold mt-1 leading-snug">{ticket.subject}</h1>
                <p className="text-sm text-muted-foreground mt-2">Bonjour {ticket.client_name}, aidez-nous à améliorer notre support en évaluant chaque aspect ci-dessous.</p>

                {questions.map((q) => (
                  <ScaleRow key={q.id}
                    label={q.label}
                    testid={`q-${q.id}`}
                    style={ticket.scale_style || "stars"}
                    labels={ticket.scale_labels || DEFAULT_LABELS}
                    value={answers[q.id] || 0}
                    onChange={(v) => setAnswers({ ...answers, [q.id]: v })}/>
                ))}

                {allAnswered && (
                  <div className="mt-6 p-3 rounded-md bg-muted/40 border text-sm text-center">
                    Note globale : <b className="mono">{overall}/5</b> — {(ticket.scale_labels || DEFAULT_LABELS)[overall]}
                  </div>
                )}

                <div className="mt-6">
                  <label className="label-caps">Commentaire (facultatif)</label>
                  <Textarea
                    data-testid="survey-comment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Un mot sur ce qui a été bien fait ou ce qui pourrait être amélioré…"
                    className="mt-2 min-h-[100px]"
                  />
                </div>
                <Button
                  disabled={!allAnswered || busy}
                  onClick={submit}
                  className="w-full mt-6"
                  data-testid="survey-submit"
                >
                  {busy ? "Envoi…" : "Envoyer mon évaluation"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      <footer className="border-t py-4 text-center text-xs text-muted-foreground">
        <Link to="/portail" className="hover:underline">Portail MasterTicket Support IT</Link>
      </footer>
    </div>
  );
}
